import { forwardRef } from "react";
import { cn } from "@/nucleo/utilitarios";
import { type LucideIcon, Inbox, MessageSquareDashed, Search, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/ui/base/button";

export type EmptyStateVariant = "default" | "messages" | "search" | "success" | "error";

interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  className?: string;
  /** Variante visual com ilustração leve (acolhedor para Mensagens, Busca, Sucesso, Erro). */
  variant?: EmptyStateVariant;
}

const VARIANT_MAP: Record<EmptyStateVariant, { icon: LucideIcon; tone: string }> = {
  default: { icon: Inbox, tone: "bg-muted text-muted-foreground" },
  messages: { icon: MessageSquareDashed, tone: "bg-primary/10 text-primary" },
  search: { icon: Search, tone: "bg-muted text-muted-foreground" },
  success: { icon: CheckCircle2, tone: "bg-success/10 text-success" },
  error: { icon: AlertCircle, tone: "bg-destructive/10 text-destructive" },
};

export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(
  ({ icon, title, description, actionLabel, onAction, className, variant = "default" }, ref) => {
    const variantConfig = VARIANT_MAP[variant];
    const Icon = icon ?? variantConfig.icon;
    return (
      <div
        ref={ref}
        className={cn("flex flex-col items-center justify-center py-16 px-4 text-center", className)}
      >
        <div className={cn("rounded-full p-4 mb-4 ring-4 ring-background", variantConfig.tone)}>
          <Icon className="h-8 w-8" />
        </div>
        <h3 className="text-lg font-semibold mb-1">{title}</h3>
        {description && <p className="text-sm text-muted-foreground max-w-sm mb-4">{description}</p>}
        {actionLabel && onAction && (
          <Button onClick={onAction}>{actionLabel}</Button>
        )}
      </div>
    );
  }
);
EmptyState.displayName = "EmptyState";
